import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";

const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const FORM_BG = "#F8F4EE";
const BORDER = "#E3D7C8";
const WHITE = "#FFFFFF";

const FF_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FF_DM = "'DM Sans', system-ui, sans-serif";
const FF_CONTRACT = "Georgia, 'Times New Roman', serif";

const LS_PROMOTEUR_INFO = "yoma_promoteur_info";
const LS_PROMOTEUR_LEGACY = "yoma_vefa_promoteur";
const LS_PROMOTEUR_LOGO = "yoma_promoteur_logo";
const LS_DOSSIER = "yoma_vefa_dossier";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_MIME_OK = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];

const EMPTY_PROMOTEUR = {
  raisonSociale: "",
  capital: "",
  siegeSocial: "",
  ville: "",
  rc: "",
  ice: "",
  email: "",
  representant: "",
  banque: "",
  agence: "",
  iban: "",
  swift: "",
  logo: "",
};

function promoteurSansLogo(p) {
  if (!p || typeof p !== "object") return {};
  const { logo: _l, ...rest } = p;
  return rest;
}

function logoSrcForHtmlAttr(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return "";
  return dataUrl.replace(/"/g, "&quot;");
}

function loadInitialPromoteur() {
  const logoStored = readLs(LS_PROMOTEUR_LOGO, "");
  const dossier = readJsonLs(LS_DOSSIER, {});
  const bankFromDossier = {
    banque: dossier.banque || "",
    agence: dossier.agence || "",
    iban: dossier.iban || "",
    swift: dossier.swift || "",
  };
  try {
    const saved = localStorage.getItem(LS_PROMOTEUR_INFO);
    if (saved) {
      const p = JSON.parse(saved);
      const logoFromJson = p.logo;
      return {
        ...EMPTY_PROMOTEUR,
        ...bankFromDossier,
        ...promoteurSansLogo(p),
        logo: logoStored || logoFromJson || "",
      };
    }
    const legacy = localStorage.getItem(LS_PROMOTEUR_LEGACY);
    if (legacy) {
      const o = JSON.parse(legacy);
      return {
        ...EMPTY_PROMOTEUR,
        ...bankFromDossier,
        raisonSociale: o.raison_sociale || localStorage.getItem("yoma_company_name") || "",
        capital: o.capital_social || "",
        siegeSocial: o.siege_social || "",
        ville: o.ville_rc || "",
        rc: o.rc || "",
        ice: o.ice || "",
        email: o.email || "",
        representant: o.representant || "",
        logo: logoStored || "",
      };
    }
  } catch {
    /* ignore */
  }
  return {
    ...EMPTY_PROMOTEUR,
    ...bankFromDossier,
    raisonSociale: localStorage.getItem("yoma_company_name") || "",
    logo: logoStored || "",
  };
}

function readLs(key, fallback = "") {
  try {
    const v = localStorage.getItem(key);
    return v != null ? v : fallback;
  } catch {
    return fallback;
  }
}

function readJsonLs(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (!v) return fallback;
    return { ...fallback, ...JSON.parse(v) };
  } catch {
    return fallback;
  }
}

function writeLs(key, val) {
  try {
    localStorage.setItem(key, typeof val === "string" ? val : JSON.stringify(val));
  } catch {
    /* ignore */
  }
}

const VERSEMENT_LABELS_DEF = ["À la signature", "Échéance 1", "Échéance 2", "Échéance 3", "À la livraison"];

const DEFAULT_VERSEMENTS = [
  { id: "v1", num: 1, label: "À la signature", pct: 30, date: "", montant_reel: "" },
  { id: "v2", num: 2, label: "Échéance 1", pct: 25, date: "", montant_reel: "" },
  { id: "v3", num: 3, label: "Échéance 2", pct: 25, date: "", montant_reel: "" },
  { id: "v4", num: 4, label: "Échéance 3", pct: 15, date: "", montant_reel: "" },
  { id: "v5", num: 5, label: "À la livraison", pct: 5, date: "", montant_reel: "" },
];

const RED_MANQUE = "#C0392B";
const VERT_EXC = "#1e8449";

/**
 * report[0]=0. Pour i>1 : report[i] = montant_prevu[i-1] - montant_reel[i-1].
 * montant_du[i] = montant_prevu[i] + report[i].
 */
function computeVersementsDerive(prixTotal, rows) {
  if (!rows?.length) return [];
  return rows.map((v, i) => {
    const pct = Number(v.pct) || 0;
    const montant_prevu = prixTotal > 0 ? (prixTotal * pct) / 100 : 0;
    let reportIncoming = 0;
    if (i > 0) {
      const prev = rows[i - 1];
      const prevPct = Number(prev.pct) || 0;
      const prevPrevu = prixTotal > 0 ? (prixTotal * prevPct) / 100 : 0;
      const prevReel = parseFloat(prev.montant_reel) || 0;
      reportIncoming = prevPrevu - prevReel;
    }
    const montant_du = montant_prevu + reportIncoming;
    const reel = parseFloat(v.montant_reel) || 0;
    return {
      pct,
      montant_prevu,
      reportIncoming,
      montant_du,
      montant_reel: reel,
    };
  });
}

function initVersementsFromStorage() {
  const saved = readJsonLs("yoma_vefa_versements", null);
  const rows =
    saved && Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_VERSEMENTS.map((x) => ({ ...x }));
  return rows.map((v, i) => {
    const num = v.num ?? i + 1;
    let mr = v.montant_reel;
    if (mr === undefined || mr === null || mr === "") {
      if (v.montant_saisi !== undefined && v.montant_saisi !== null && v.montant_saisi !== "") mr = v.montant_saisi;
      else if (v.montant !== undefined && v.montant !== null && v.montant !== "") mr = v.montant;
      else mr = "";
    }
    let montant_reelNorm = "";
    if (mr !== "" && mr != null) {
      const n = typeof mr === "number" ? mr : parseFloat(String(mr).replace(/\s/g, "").replace(",", "."));
      montant_reelNorm = Number.isFinite(n) ? Math.round(n * 100) / 100 : "";
    }
    return {
      id: v.id || `v${num}`,
      num,
      label: v.label || VERSEMENT_LABELS_DEF[i] || `Versement ${num}`,
      pct: v.pct ?? DEFAULT_VERSEMENTS[i]?.pct ?? 0,
      date: v.date ?? "",
      montant_reel: montant_reelNorm,
    };
  });
}

const TYPE_ACHETEUR_PHYSIQUE = "physique";
const TYPE_ACHETEUR_COMMUN = "commun";
const TYPE_ACHETEUR_MORALE = "morale";

function newCoAcquereur(suffix) {
  return {
    id: `ca-${Date.now()}-${suffix}`,
    civilite: "M.",
    nom: "",
    prenom: "",
    date_naissance: "",
    lieu_naissance: "",
    adresse_residence: "",
    type_piece: "CIN",
    num_piece: "",
    nationalite: "",
  };
}

function normalizeDossierLoaded(raw) {
  const d = { ...DEFAULT_DOSSIER, ...raw };
  const typesOk = [TYPE_ACHETEUR_PHYSIQUE, TYPE_ACHETEUR_COMMUN, TYPE_ACHETEUR_MORALE];
  if (!d.type_acheteur || !typesOk.includes(d.type_acheteur)) d.type_acheteur = TYPE_ACHETEUR_PHYSIQUE;
  if (!Array.isArray(d.coacquereurs)) d.coacquereurs = [];
  if (d.type_acheteur === TYPE_ACHETEUR_COMMUN) {
    const ca = d.coacquereurs.map((c, i) => ({
      ...newCoAcquereur(`l${i}`),
      ...c,
      id: c?.id || `ca-${i}`,
    }));
    while (ca.length < 2) ca.push(newCoAcquereur(ca.length));
    d.coacquereurs = ca;
  }
  return d;
}

function formatListeNomsCoacquereurs(list) {
  return (list || [])
    .map((c) => [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim())
    .filter(Boolean);
}

function joinFrEt(parts) {
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} et ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}`;
}

const DEFAULT_DOSSIER = {
  type_acheteur: TYPE_ACHETEUR_PHYSIQUE,
  adresse_commune: "",
  coacquereurs: [],
  acheteur_morale_raison_sociale: "",
  acheteur_morale_forme_juridique: "SARL",
  acheteur_morale_forme_autre: "",
  acheteur_morale_capital: "",
  acheteur_morale_siege: "",
  acheteur_morale_rc: "",
  acheteur_morale_ice: "",
  acheteur_morale_representant_prenom: "",
  acheteur_morale_representant_nom: "",
  acheteur_morale_qualite: "",
  acheteur_morale_email: "",
  acheteur_morale_telephone: "",
  civilite: "M.",
  nom: "",
  prenom: "",
  date_naissance: "",
  lieu_naissance: "",
  adresse_residence: "",
  type_piece: "CIN",
  num_piece: "",
  nationalite: "",
  email_reservataire: "",
  telephone: "",
  nom_projet: "",
  num_villa: "",
  num_lot: "",
  num_lot_foncier: "",
  surface_terrain: "",
  surface_rdc: "",
  surface_etage: "",
  surface_ssol: "",
  adresse_projet: "",
  ref_fonciere: "",
  type_villa: "Villa RDC",
  type_villa_autre: "",
  style_architectural: "Contemporain",
  style_architectural_autre: "",
  nb_chambres: "",
  nb_sdb: "",
  villa_piscine: false,
  villa_jardin: false,
  villa_parking: false,
  villa_sous_sol: false,
  equipements: "",
  finitions: "Standard",
  prix_total: "",
  banque: "",
  agence: "",
  iban: "",
  swift: "",
  delai_livraison_mois: "18",
  date_demarrage_travaux: "",
  prorogation_mois: "6",
  indemnite_resiliation_pct: "10",
  clause_arbitrage: true,
  nom_arbitre: "",
  siege_arbitrage: "",
  langue_arbitrage: "français",
  honoraires_arbitre_pct: "3.6",
  clause_ia_personnalisee: "",
};

const TEXTE_ARTICLE_1 = `Le présent contrat a pour objet la réservation par le Réservataire d'un droit d'acquérir, en vente en l'état futur d'achèvement (VEFA), la villa individuelle désignée ci-après, dans le cadre du projet immobilier mentionné aux présentes, selon les modalités, le prix et l'échéancier convenus. La signature des présentes vaut réservation ferme sous réserve du respect des obligations de paiement et des conditions suspensives éventuellement prévues par la réglementation applicable.`;

const TEXTE_ARTICLES_5_11 = `ARTICLE 5 — CONDITIONS SUSPENSIVES
Les obligations du Promoteur et du Réservataire sont subordonnées, le cas échéant, à l'obtention des autorisations administratives et des financements nécessaires à la réalisation de l'opération, dans les délais raisonnables fixés par la réglementation.

ARTICLE 6 — GARANTIES DU PROMOTEUR
Le Promoteur déclare disposer des droits nécessaires pour promouvoir le projet et s'engage à transmettre au Réservataire un bien conforme aux prescriptions légales et contractuelles en vigueur à la date de livraison.

ARTICLE 7 — DROIT DE RÉTRACTATION ET RÉSILIATION
Toute résiliation est régie par les stipulations du présent contrat et par la loi applicable. En cas de manquement grave, la partie lésée pourra demander la résiliation judiciaire ou conventionnelle avec indemnisation selon les barèmes et usages du secteur.

ARTICLE 8 — PENALITES ET INDEMNITES
Les pénalités éventuelles pour retard de livraison ou défaut de paiement sont celles prévues aux présentes ou, à défaut, par la loi. Les parties pourront convenir d'une indemnité forfaitaire ou proportionnelle dans la limite des dispositions d'ordre public.

ARTICLE 9 — ATTRIBUTION DE JURIDICTION (HORS ARBITRAGE)
À défaut de clause d'arbitrage applicable ou pour les mesures conservatoires, les tribunaux matériellement compétents seront ceux du siège du Promoteur, sous réserve des règles impératives de compétence.

ARTICLE 10 — FISCALITE ET FRAIS
Les droits d'enregistrement, frais de notaire et taxes afférents à la vente définitive sont à la charge du Réservataire ou répartis conformément aux usages et à l'acte authentique de vente, sauf convention contraire.

ARTICLE 11 — DISPOSITIONS FINALES
Les présentes annulent et remplacent toute négociation antérieure non formalisée. Toute modification devra faire l'objet d'un avenant écrit signé des parties. Le contrat est exécutoire à compter de sa signature sous réserve des conditions suspensives éventuelles.`;

function formatMoney(n) {
  if (n === "" || n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatDateFr(iso) {
  if (!iso) return "………………";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}

export default function ContratAcquereurPage() {
  const [promoteur, setPromoteur] = useState(() => loadInitialPromoteur());
  const [promoteurSaveMsg, setPromoteurSaveMsg] = useState("");

  const [d, setD] = useState(() => normalizeDossierLoaded(readJsonLs(LS_DOSSIER, { ...DEFAULT_DOSSIER })));
  const [versements, setVersements] = useState(() => initVersementsFromStorage());

  const [iaPrompt, setIaPrompt] = useState("");
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState("");

  useEffect(() => {
    const id = "yoma-fonts-contrat-vefa";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    writeLs(LS_DOSSIER, d);
  }, [d]);

  useEffect(() => {
    writeLs("yoma_vefa_versements", versements);
  }, [versements]);

  const prixNum = useMemo(() => {
    const n = parseFloat(String(d.prix_total).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }, [d.prix_total]);

  const versementDerive = useMemo(() => computeVersementsDerive(prixNum, versements), [prixNum, versements]);

  const sumPct = useMemo(
    () => versements.reduce((s, v) => s + (Number(v.pct) || 0), 0),
    [versements]
  );

  const pctOk = Math.abs(sumPct - 100) < 0.01;

  const totalVerseReel = useMemo(
    () => versements.reduce((s, v) => s + (parseFloat(v.montant_reel) || 0), 0),
    [versements]
  );

  const resteAPayer = useMemo(() => {
    if (!prixNum) return 0;
    return Math.round((prixNum - totalVerseReel) * 100) / 100;
  }, [prixNum, totalVerseReel]);

  const progressionPct = useMemo(() => {
    if (!prixNum || prixNum <= 0) return 0;
    return Math.min(100, Math.max(0, (totalVerseReel / prixNum) * 100));
  }, [prixNum, totalVerseReel]);

  const coordonneesBanque = useMemo(() => {
    const b = promoteur.banque || d.banque;
    const ag = promoteur.agence || d.agence;
    const ib = promoteur.iban || d.iban;
    const sw = promoteur.swift || d.swift;
    const parts = [b, ag, ib, sw].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }, [promoteur.banque, promoteur.agence, promoteur.iban, promoteur.swift, d.banque, d.agence, d.iban, d.swift]);

  const nomReservataireComplet = useMemo(() => {
    const t = d.type_acheteur || TYPE_ACHETEUR_PHYSIQUE;
    if (t === TYPE_ACHETEUR_COMMUN) {
      const parts = formatListeNomsCoacquereurs(d.coacquereurs);
      return joinFrEt(parts.length ? parts : ["………………"]);
    }
    if (t === TYPE_ACHETEUR_MORALE) {
      const rs = d.acheteur_morale_raison_sociale || "………………";
      const rep = [d.acheteur_morale_representant_prenom, d.acheteur_morale_representant_nom].filter(Boolean).join(" ").trim();
      return rep ? `${rs} — ${rep}` : rs;
    }
    return [d.civilite, d.prenom, d.nom].filter(Boolean).join(" ").trim() || "………………";
  }, [d]);

  const labelSignatureReservataire =
    d.type_acheteur === TYPE_ACHETEUR_COMMUN ? "Les Réservataires" : "Le Réservataire";

  const dateToday = useMemo(() => new Date().toLocaleDateString("fr-FR"), []);

  const contratHtml = useMemo(() => {
    const echRowsHtml = versementDerive
      .map((vd, i) => {
        const row = versements[i];
        const n = i + 1;
        let when = "";
        if (i === 0) when = "à la signature";
        else if (i === versementDerive.length - 1) when = "à la livraison";
        else when = row?.date ? `le ${formatDateFr(row.date)}` : "………………";
        return `<li>V${n} : ${vd.pct}% = ${formatMoney(vd.montant_du)} MAD ${when}</li>`;
      })
      .join("\n    ");

    const art12 = d.clause_arbitrage
      ? `<p><strong>ARTICLE 12 : ARBITRAGE</strong></p>
         <p>Les parties conviennent de recourir à l'arbitrage conformément à la loi n° 95-17 relative à l'arbitrage et à la médiation conventionnelle.</p>
         <p>Arbitre désigné : ${escapeHtml(d.nom_arbitre || "………………")}<br/>
         Siège de l'arbitrage : ${escapeHtml(d.siege_arbitrage || "………………")}<br/>
         Langue : ${escapeHtml(d.langue_arbitrage || "français")}<br/>
         Honoraires d'arbitrage : ${escapeHtml(d.honoraires_arbitre_pct || "3.6")}% du montant du litige.</p>`
      : `<p><strong>ARTICLE 12 : ARBITRAGE</strong></p><p>La présente clause est expressément écartée par accord des parties.</p>`;

    const extra = d.clause_ia_personnalisee
      ? `<section style="margin-top:18px;padding-top:12px;border-top:1px solid #ddd"><p><strong>CLAUSE ADDITIONNELLE (IA)</strong></p><div style="white-space:pre-wrap">${escapeHtml(
          d.clause_ia_personnalisee
        )}</div></section>`
      : "";

    const logoBlock = promoteur.logo
      ? `<img src="${logoSrcForHtmlAttr(promoteur.logo)}" alt="" style="height:60px;float:right;margin:0 0 8px 12px" />`
      : "";

    return `
<div class="vefa-contract-inner">
  <div style="overflow:hidden">
  ${logoBlock}
  <p style="text-align:center;font-weight:700">VILLA N° ${escapeHtml(d.num_villa || "—")}</p>
  <h1 style="text-align:center;font-size:18px;margin:12px 0">CONTRAT DE RÉSERVATION EN VENTE<br/>À L'ÉTAT FUTUR D'ACHÈVEMENT</h1>
  <p style="text-align:center">Terrain + Villa individuelle clé en main</p>
  <p style="text-align:center;font-style:italic">Projet Immobilier « ${escapeHtml(d.nom_projet || "—")} »</p>
  <p style="margin-top:20px"><strong>ENTRE LES SOUSSIGNÉS :</strong></p>
  <p>La Société « ${escapeHtml(promoteur.raisonSociale || "—")} », SARL au capital de ${escapeHtml(
      promoteur.capital || "—"
    )} MAD, siège social sis à ${escapeHtml(promoteur.siegeSocial || "—")}, immatriculée au RC de ${escapeHtml(
      promoteur.ville || "—"
    )} sous n° ${escapeHtml(promoteur.rc || "—")}, ICE n° : ${escapeHtml(promoteur.ice || "—")}</p>
  <p>Représentée aux fins des présentes par ${escapeHtml(promoteur.representant || "—")}</p>
  <p>Désignée ci-après comme le « Promoteur immobilier ».</p>
  ${(() => {
    const t = d.type_acheteur || TYPE_ACHETEUR_PHYSIQUE;
    if (t === TYPE_ACHETEUR_COMMUN) {
      const parts = (d.coacquereurs || []).map((c) => {
        const s = [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim();
        return escapeHtml(s || "………………");
      });
      const ligneNoms = joinFrEt(parts.length >= 2 ? parts : [...parts, "………………", "………………"].slice(0, 2));
      const addr = escapeHtml(d.adresse_commune || "………………");
      return `<p><strong>Et</strong> ${ligneNoms}, résidant à ${addr},</p>
  <p>Désignés ci-après comme les « Réservataires ».</p>`;
    }
    if (t === TYPE_ACHETEUR_MORALE) {
      const formeBrute =
        d.acheteur_morale_forme_juridique === "Autre"
          ? d.acheteur_morale_forme_autre || "—"
          : d.acheteur_morale_forme_juridique || "—";
      const rep = [d.acheteur_morale_representant_prenom, d.acheteur_morale_representant_nom]
        .filter(Boolean)
        .join(" ")
        .trim();
      return `<p><strong>Et</strong> La Société « ${escapeHtml(d.acheteur_morale_raison_sociale || "—")} », ${escapeHtml(
        formeBrute
      )} au capital de ${escapeHtml(d.acheteur_morale_capital || "—")} MAD, siège social sis à ${escapeHtml(
        d.acheteur_morale_siege || "—"
      )}, immatriculée sous n° ${escapeHtml(d.acheteur_morale_rc || "—")}, ICE : ${escapeHtml(
        d.acheteur_morale_ice || "—"
      )}</p>
  <p>Représentée par ${escapeHtml(rep || "………………")}, en qualité de ${escapeHtml(d.acheteur_morale_qualite || "—")}</p>
  <p>Désignée ci-après comme le « Réservataire ».</p>`;
    }
    return `<p><strong>Et</strong> ${escapeHtml(d.civilite || "")} ${escapeHtml(d.nom || "")} ${escapeHtml(
      d.prenom || ""
    )}. Né(e) le ${escapeHtml(d.date_naissance || "—")} à ${escapeHtml(
      d.lieu_naissance || "—"
    )}, résident à ${escapeHtml(d.adresse_residence || "—")}, titulaire du ${escapeHtml(
      d.type_piece || "—"
    )} N° ${escapeHtml(d.num_piece || "—")}</p>
  <p>Désigné ci-après comme le « Réservataire ».</p>`;
  })()}
  <p><strong>ARTICLE 1 : OBJET DU CONTRAT</strong></p>
  <p>${TEXTE_ARTICLE_1}</p>
  <p><strong>ARTICLE 2 : DÉSIGNATION DU BIEN</strong></p>
  <ul style="margin:0;padding-left:20px">
    <li>Superficie terrain : ${escapeHtml(d.surface_terrain || "—")} m²</li>
    <li>Villa : ${escapeHtml(d.num_villa || "—")}</li>
    <li>N° de lot : ${escapeHtml(d.num_lot || "—")}</li>
    <li>Surface RDC : ${escapeHtml(d.surface_rdc || "—")} m²</li>
    <li>Surface Étage : ${escapeHtml(d.surface_etage || "—")} m²</li>
    <li>Surface S-SOL : ${escapeHtml(d.surface_ssol || "—")} m²</li>
    <li>Projet : ${escapeHtml(d.nom_projet || "—")}</li>
    <li>Adresse : ${escapeHtml(d.adresse_projet || "—")}</li>
    <li>Référence foncière : ${escapeHtml(d.ref_fonciere || "—")}</li>
  </ul>
  <p style="margin-top:12px"><strong>Identification de la Villa :</strong></p>
  <ul style="margin:0;padding-left:20px">
    <li>Type : ${escapeHtml(
      d.type_villa === "Autre" ? d.type_villa_autre || "—" : d.type_villa || "—"
    )}</li>
    <li>Style : ${escapeHtml(
      d.style_architectural === "Autre" ? d.style_architectural_autre || "—" : d.style_architectural || "—"
    )}</li>
    <li>Chambres : ${escapeHtml(d.nb_chambres || "—")}</li>
    <li>Salles de bain : ${escapeHtml(d.nb_sdb || "—")}</li>
    <li>Équipements : ${escapeHtml(d.equipements || "—")}</li>
    <li>Finitions : ${escapeHtml(d.finitions || "—")}</li>
  </ul>
  <p><strong>ARTICLE 3 : PRIX ET MODALITÉS</strong></p>
  <p>Prix de vente : ${formatMoney(prixNum || d.prix_total)} MAD</p>
  <p><strong>Échéancier :</strong></p>
  <ul style="margin:0;padding-left:20px">
    ${echRowsHtml}
  </ul>
  <p>Coordonnées bancaires : ${escapeHtml(coordonneesBanque)}</p>
  <p><strong>ARTICLE 4 : DÉLAI DE LIVRAISON</strong></p>
  <p>${escapeHtml(d.delai_livraison_mois || "—")} mois à compter du démarrage des travaux (${escapeHtml(
      d.date_demarrage_travaux ? formatDateFr(d.date_demarrage_travaux) : "date de démarrage à préciser"
    )}). Prorogation possible de ${escapeHtml(d.prorogation_mois || "—")} mois.</p>
  <p>Indemnité de résiliation conventionnelle : ${escapeHtml(d.indemnite_resiliation_pct || "10")}% du prix, sauf disposition légale contraire.</p>
  <div style="white-space:pre-wrap;font-size:13px;margin-top:16px">${TEXTE_ARTICLES_5_11.replace(
    /\n/g,
    "<br/>"
  )}</div>
  <div style="margin-top:16px">${art12}</div>
  ${extra}
  <p style="margin-top:32px;display:flex;justify-content:space-between"><span>${escapeHtml(
    labelSignatureReservataire
  )}<br/><br/>${escapeHtml(nomReservataireComplet)}</span><span>Le Promoteur<br/><br/>${escapeHtml(
    promoteur.representant || "—"
  )}</span></p>
  <p style="margin-top:24px">Fait à ${escapeHtml(promoteur.ville || promoteur.siegeSocial || "………………")} le ${dateToday}</p>
  </div>
</div>`;
  }, [
    d,
    promoteur,
    versementDerive,
    versements,
    coordonneesBanque,
    nomReservataireComplet,
    labelSignatureReservataire,
    dateToday,
    prixNum,
  ]);

  const contratTexteBrut = useMemo(() => stripHtmlToText(contratHtml), [contratHtml]);

  const updateD = useCallback((patch) => {
    setD((prev) => ({ ...prev, ...patch }));
  }, []);

  const onTypeAcheteurChange = useCallback((v) => {
    if (v === TYPE_ACHETEUR_COMMUN) {
      setD((prev) => {
        let ca = Array.isArray(prev.coacquereurs) ? [...prev.coacquereurs] : [];
        while (ca.length < 2) ca.push(newCoAcquereur(ca.length));
        return { ...prev, type_acheteur: v, coacquereurs: ca };
      });
    } else {
      updateD({ type_acheteur: v });
    }
  }, [updateD]);

  const updateCoAcquereur = useCallback((index, patch) => {
    setD((prev) => {
      const next = [...(prev.coacquereurs || [])];
      if (!next[index]) return prev;
      next[index] = { ...next[index], ...patch };
      return { ...prev, coacquereurs: next };
    });
  }, []);

  const addCoAcquereur = useCallback(() => {
    setD((prev) => ({
      ...prev,
      coacquereurs: [...(prev.coacquereurs || []), newCoAcquereur((prev.coacquereurs || []).length)],
    }));
  }, []);

  const removeCoAcquereur = useCallback((index) => {
    setD((prev) => {
      const ca = [...(prev.coacquereurs || [])];
      if (ca.length <= 2) return prev;
      ca.splice(index, 1);
      return { ...prev, coacquereurs: ca };
    });
  }, []);

  const updatePromoteur = useCallback((patch) => {
    setPromoteur((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateVersement = useCallback((index, patch) => {
    setVersements((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const handleMontantReelChange = useCallback(
    (index, valStr) => {
      if (valStr === "" || String(valStr).trim() === "") {
        updateVersement(index, { montant_reel: "" });
        return;
      }
      const ms = parseFloat(String(valStr).replace(/\s/g, "").replace(",", "."));
      if (!Number.isFinite(ms)) {
        updateVersement(index, { montant_reel: "" });
        return;
      }
      updateVersement(index, { montant_reel: Math.round(ms * 100) / 100 });
    },
    [updateVersement]
  );

  const handleVersementPctChange = useCallback((index, pctStr) => {
    updateVersement(index, { pct: pctStr });
  }, [updateVersement]);

  const genererClauseIA = async () => {
    if (!iaPrompt.trim()) return;
    setIaLoading(true);
    setIaError("");
    try {
      const system =
        "Tu es juriste immobilier marocain spécialisé VEFA. Réponds uniquement par le texte de la clause, formel, en français.";
      const user = `Rédige une clause contractuelle courte (maximum 200 mots) pour un contrat de réservation VEFA. Demande du client : ${iaPrompt.trim()}`;
      const { data, error } = await supabase.functions.invoke("generate-ai", {
        body: { system, user },
      });
      if (error) throw error;
      const text =
        data?.data?.content?.[0]?.text || data?.content?.[0]?.text || data?.text || "Réponse vide.";
      updateD({ clause_ia_personnalisee: (d.clause_ia_personnalisee ? d.clause_ia_personnalisee + "\n\n" : "") + text.trim() });
      setIaPrompt("");
    } catch (e) {
      setIaError(e?.message || String(e));
    } finally {
      setIaLoading(false);
    }
  };

  const copierContrat = async () => {
    try {
      await navigator.clipboard.writeText(contratTexteBrut);
      alert("Contrat copié dans le presse-papiers.");
    } catch {
      alert("Copie impossible.");
    }
  };

  const versionImprimable = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Contrat VEFA</title>
      <style>
        body { font-family: ${FF_CONTRACT}; color: ${TAUPE}; padding: 40px; max-width: 800px; margin: 0 auto; }
        @media print { body { padding: 12mm; } }
      </style></head><body>${contratHtml}</body></html>`);
    w.document.close();
    w.onload = () => {
      w.focus();
      w.print();
    };
  };

  const enregistrerInfosPromoteur = useCallback(() => {
    try {
      localStorage.setItem(LS_PROMOTEUR_INFO, JSON.stringify(promoteurSansLogo(promoteur)));
      if (promoteur.raisonSociale) writeLs("yoma_company_name", promoteur.raisonSociale);
      setD((prev) => ({
        ...prev,
        banque: promoteur.banque,
        agence: promoteur.agence,
        iban: promoteur.iban,
        swift: promoteur.swift,
      }));
      setPromoteurSaveMsg("Informations promoteur enregistrées ✓");
      window.setTimeout(() => setPromoteurSaveMsg(""), 4000);
    } catch {
      alert("Enregistrement impossible.");
    }
  }, [promoteur]);

  const enregistrerDossier = () => {
    writeLs(LS_DOSSIER, {
      ...d,
      banque: promoteur.banque,
      agence: promoteur.agence,
      iban: promoteur.iban,
      swift: promoteur.swift,
    });
    writeLs(LS_PROMOTEUR_INFO, promoteurSansLogo(promoteur));
    writeLs("yoma_vefa_versements", versements);
    alert("Dossier VEFA enregistré localement.");
  };

  const scrollPreview = () => {
    const el = document.getElementById("vefa-apercu");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goBranding = () => {
    alert(
      "Pour modifier la raison sociale et l'identité d'entreprise : menu latéral Outils → Branding / Intégrations."
    );
  };

  const logoFileInputRef = useRef(null);

  const traiterFichierLogo = useCallback((file) => {
    if (!file) return;
    const type = (file.type || "").toLowerCase();
    const ok =
      LOGO_MIME_OK.includes(type) ||
      (type === "image/svg+xml" || file.name?.toLowerCase().endsWith(".svg"));
    if (!ok) {
      alert("Formats acceptés : PNG, JPG, SVG.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      alert("Fichier trop volumineux (max 2 Mo).");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setPromoteur((p) => ({ ...p, logo: base64 }));
      try {
        localStorage.setItem(LS_PROMOTEUR_LOGO, base64);
      } catch {
        alert("Stockage local insuffisant ou quota dépassé.");
      }
    };
    reader.readAsDataURL(file);
  }, []);

  const onLogoInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) traiterFichierLogo(file);
      e.target.value = "";
    },
    [traiterFichierLogo]
  );

  const onLogoZoneDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer?.files?.[0];
      if (file) traiterFichierLogo(file);
    },
    [traiterFichierLogo]
  );

  const supprimerLogoPromoteur = useCallback(() => {
    setPromoteur((p) => ({ ...p, logo: "" }));
    try {
      localStorage.removeItem(LS_PROMOTEUR_LOGO);
    } catch {
      /* ignore */
    }
  }, []);

  const cardStyle = {
    background: WHITE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: 18,
    marginBottom: 14,
  };

  const label = (t) => (
    <label style={{ display: "block", fontFamily: FF_DM, fontSize: 10, color: "#8B7361", marginBottom: 4, fontWeight: 600 }}>
      {t}
    </label>
  );

  const input = (props) => (
    <input
      {...props}
      style={{
        width: "100%",
        padding: "8px 10px",
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        fontFamily: FF_DM,
        fontSize: 13,
        boxSizing: "border-box",
        ...props.style,
      }}
    />
  );

  return (
    <main
      className="vefa-page"
      style={{
        minHeight: "100vh",
        background: FORM_BG,
        padding: 24,
        fontFamily: FF_DM,
        color: TAUPE,
      }}
    >
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-contract { display: block !important; position: static !important; width: 100% !important; max-width: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <header className="no-print" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 32, color: TAUPE }}>Contrat de Réservation VEFA</h1>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "#8B7361" }}>Vente en l&apos;État Futur d&apos;Achèvement</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={scrollPreview}
              style={{
                padding: "10px 18px",
                border: "none",
                borderRadius: 8,
                background: GOLD,
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FF_DM,
                fontSize: 13,
              }}
            >
              Générer le contrat
            </button>
            <button
              type="button"
              onClick={versionImprimable}
              style={{
                padding: "10px 18px",
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                background: WHITE,
                color: TAUPE,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: FF_DM,
                fontSize: 13,
              }}
            >
              Version imprimable
            </button>
          </div>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(320px,1fr)", gap: 20, alignItems: "start" }}>
        {/* Colonne formulaire */}
        <div className="no-print" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* A — Promoteur */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Informations Promoteur</h2>
              <button type="button" onClick={goBranding} style={{ fontSize: 12, color: GOLD, background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>
                Modifier
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>{label("Raison sociale")}{input({ value: promoteur.raisonSociale, onChange: (e) => updatePromoteur({ raisonSociale: e.target.value }) })}</div>
              <div>{label("Capital social (MAD)")}{input({ value: promoteur.capital, onChange: (e) => updatePromoteur({ capital: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("Siège social")}{input({ value: promoteur.siegeSocial, onChange: (e) => updatePromoteur({ siegeSocial: e.target.value }) })}</div>
              <div>{label("Ville (RC)")}{input({ value: promoteur.ville, onChange: (e) => updatePromoteur({ ville: e.target.value }) })}</div>
              <div>{label("RC")}{input({ value: promoteur.rc, onChange: (e) => updatePromoteur({ rc: e.target.value }) })}</div>
              <div>{label("ICE")}{input({ value: promoteur.ice, onChange: (e) => updatePromoteur({ ice: e.target.value }) })}</div>
              <div>{label("Email")}{input({ type: "email", value: promoteur.email, onChange: (e) => updatePromoteur({ email: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("Représenté par")}{input({ value: promoteur.representant, onChange: (e) => updatePromoteur({ representant: e.target.value }) })}</div>
              <div>{label("Banque")}{input({ value: promoteur.banque, onChange: (e) => updatePromoteur({ banque: e.target.value }) })}</div>
              <div>{label("Agence")}{input({ value: promoteur.agence, onChange: (e) => updatePromoteur({ agence: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("IBAN")}{input({ value: promoteur.iban, onChange: (e) => updatePromoteur({ iban: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("SWIFT")}{input({ value: promoteur.swift, onChange: (e) => updatePromoteur({ swift: e.target.value }) })}</div>
            </div>
            <div style={{ marginTop: 14 }}>
              {label("Logo promoteur")}
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,.svg"
                style={{ display: "none" }}
                onChange={onLogoInputChange}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => logoFileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    logoFileInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={onLogoZoneDrop}
                style={{
                  border: `2px dashed ${BORDER}`,
                  borderRadius: 10,
                  padding: 18,
                  textAlign: "center",
                  cursor: "pointer",
                  background: "#FAF8F5",
                  fontSize: 13,
                  color: TAUPE,
                }}
              >
                <span style={{ fontWeight: 600 }}>Cliquer pour importer le logo</span>
                <div style={{ fontSize: 11, color: "#8B7361", marginTop: 6 }}>PNG, JPG, SVG — max 2 Mo</div>
              </div>
              {promoteur.logo ? (
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <img
                    src={promoteur.logo}
                    alt="Logo promoteur"
                    style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain" }}
                  />
                  <button
                    type="button"
                    onClick={supprimerLogoPromoteur}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${BORDER}`,
                      background: WHITE,
                      color: "#B85C3A",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: FF_DM,
                      fontSize: 12,
                    }}
                  >
                    Supprimer
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={enregistrerInfosPromoteur}
              style={{
                marginTop: 14,
                padding: "10px 18px",
                border: "none",
                borderRadius: 8,
                background: GOLD,
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FF_DM,
                fontSize: 13,
                width: "100%",
              }}
            >
              Enregistrer les infos promoteur
            </button>
            {promoteurSaveMsg ? (
              <p style={{ margin: "10px 0 0", fontSize: 13, color: "#2d6a4f", fontWeight: 600 }}>{promoteurSaveMsg}</p>
            ) : null}
          </div>

          {/* B — Réservataire */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Informations Réservataire</h2>
            <div style={{ marginBottom: 14 }}>
              {label("TYPE D'ACHETEUR")}
              <select
                value={d.type_acheteur || TYPE_ACHETEUR_PHYSIQUE}
                onChange={(e) => onTypeAcheteurChange(e.target.value)}
                style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 13 }}
              >
                <option value={TYPE_ACHETEUR_PHYSIQUE}>Personne physique</option>
                <option value={TYPE_ACHETEUR_COMMUN}>Achat en commun (plusieurs personnes)</option>
                <option value={TYPE_ACHETEUR_MORALE}>Société / Personne morale</option>
              </select>
            </div>

            {d.type_acheteur === TYPE_ACHETEUR_PHYSIQUE && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  {label("Civilité")}
                  <select
                    value={d.civilite}
                    onChange={(e) => updateD({ civilite: e.target.value })}
                    style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM }}
                  >
                    <option value="M.">M.</option>
                    <option value="Mme">Mme</option>
                    <option value="M. et Mme">M. et Mme</option>
                  </select>
                </div>
                <div>{label("Nom")}{input({ value: d.nom, onChange: (e) => updateD({ nom: e.target.value }) })}</div>
                <div>{label("Prénom")}{input({ value: d.prenom, onChange: (e) => updateD({ prenom: e.target.value }) })}</div>
                <div>{label("Date de naissance")}{input({ type: "date", value: d.date_naissance, onChange: (e) => updateD({ date_naissance: e.target.value }) })}</div>
                <div>{label("Lieu de naissance")}{input({ value: d.lieu_naissance, onChange: (e) => updateD({ lieu_naissance: e.target.value }) })}</div>
                <div style={{ gridColumn: "1 / -1" }}>{label("Adresse de résidence")}{input({ value: d.adresse_residence, onChange: (e) => updateD({ adresse_residence: e.target.value }) })}</div>
                <div>
                  {label("Type pièce d'identité")}
                  <select
                    value={d.type_piece}
                    onChange={(e) => updateD({ type_piece: e.target.value })}
                    style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM }}
                  >
                    <option value="CIN">CIN</option>
                    <option value="Passeport">Passeport</option>
                    <option value="Carte de séjour">Carte de séjour</option>
                  </select>
                </div>
                <div>{label("N° pièce")}{input({ value: d.num_piece, onChange: (e) => updateD({ num_piece: e.target.value }) })}</div>
                <div>{label("Nationalité")}{input({ value: d.nationalite, onChange: (e) => updateD({ nationalite: e.target.value }) })}</div>
                <div>{label("Email")}{input({ type: "email", value: d.email_reservataire, onChange: (e) => updateD({ email_reservataire: e.target.value }) })}</div>
                <div>{label("Téléphone")}{input({ value: d.telephone, onChange: (e) => updateD({ telephone: e.target.value }) })}</div>
              </div>
            )}

            {d.type_acheteur === TYPE_ACHETEUR_COMMUN && (
              <div>
                <p style={{ fontSize: 12, color: "#8B7361", margin: "0 0 10px" }}>Minimum 2 co-acquéreurs. L&apos;adresse commune apparaît dans le contrat.</p>
                {(d.coacquereurs || []).map((ca, idx) => (
                  <div
                    key={ca.id || idx}
                    style={{
                      border: `1px solid ${BORDER}`,
                      borderRadius: 10,
                      padding: 12,
                      marginBottom: 12,
                      background: "#FAFAF8",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>Co-acquéreur {idx + 1}</span>
                      {(d.coacquereurs || []).length > 2 ? (
                        <button
                          type="button"
                          onClick={() => removeCoAcquereur(idx)}
                          style={{
                            fontSize: 12,
                            color: "#B85C3A",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Supprimer
                        </button>
                      ) : null}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        {label("Civilité")}
                        <select
                          value={ca.civilite}
                          onChange={(e) => updateCoAcquereur(idx, { civilite: e.target.value })}
                          style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM }}
                        >
                          <option value="M.">M.</option>
                          <option value="Mme">Mme</option>
                          <option value="M. et Mme">M. et Mme</option>
                        </select>
                      </div>
                      <div>{label("Nom")}{input({ value: ca.nom, onChange: (e) => updateCoAcquereur(idx, { nom: e.target.value }) })}</div>
                      <div>{label("Prénom")}{input({ value: ca.prenom, onChange: (e) => updateCoAcquereur(idx, { prenom: e.target.value }) })}</div>
                      <div>
                        {label("Date de naissance")}
                        {input({ type: "date", value: ca.date_naissance, onChange: (e) => updateCoAcquereur(idx, { date_naissance: e.target.value }) })}
                      </div>
                      <div>{label("Lieu de naissance")}{input({ value: ca.lieu_naissance, onChange: (e) => updateCoAcquereur(idx, { lieu_naissance: e.target.value }) })}</div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        {label("Adresse")}
                        {input({ value: ca.adresse_residence, onChange: (e) => updateCoAcquereur(idx, { adresse_residence: e.target.value }) })}
                      </div>
                      <div>
                        {label("Type pièce d'identité")}
                        <select
                          value={ca.type_piece}
                          onChange={(e) => updateCoAcquereur(idx, { type_piece: e.target.value })}
                          style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM }}
                        >
                          <option value="CIN">CIN</option>
                          <option value="Passeport">Passeport</option>
                          <option value="Carte de séjour">Carte de séjour</option>
                        </select>
                      </div>
                      <div>{label("N° pièce")}{input({ value: ca.num_piece, onChange: (e) => updateCoAcquereur(idx, { num_piece: e.target.value }) })}</div>
                      <div style={{ gridColumn: "1 / -1" }}>{label("Nationalité")}{input({ value: ca.nationalite, onChange: (e) => updateCoAcquereur(idx, { nationalite: e.target.value }) })}</div>
                    </div>
                  </div>
                ))}
                <div style={{ marginBottom: 12 }}>
                  {label("Adresse commune (contrat)")}
                  {input({ value: d.adresse_commune, onChange: (e) => updateD({ adresse_commune: e.target.value }), placeholder: "Adresse affichée dans le contrat pour tous les co-acquéreurs" })}
                </div>
                <button
                  type="button"
                  onClick={addCoAcquereur}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: `1px dashed ${GOLD}`,
                    background: WHITE,
                    color: GOLD,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                    fontSize: 12,
                  }}
                >
                  + Ajouter un co-acquéreur
                </button>
              </div>
            )}

            {d.type_acheteur === TYPE_ACHETEUR_MORALE && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ gridColumn: "1 / -1" }}>{label("Raison sociale")}{input({ value: d.acheteur_morale_raison_sociale, onChange: (e) => updateD({ acheteur_morale_raison_sociale: e.target.value }) })}</div>
                <div>
                  {label("Forme juridique")}
                  <select
                    value={d.acheteur_morale_forme_juridique}
                    onChange={(e) => updateD({ acheteur_morale_forme_juridique: e.target.value })}
                    style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM }}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SA">SA</option>
                    <option value="SAS">SAS</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                {d.acheteur_morale_forme_juridique === "Autre" ? (
                  <div style={{ gridColumn: "1 / -1" }}>
                    {label("Préciser la forme")}
                    {input({ value: d.acheteur_morale_forme_autre, onChange: (e) => updateD({ acheteur_morale_forme_autre: e.target.value }) })}
                  </div>
                ) : null}
                <div>{label("Capital social (MAD)")}{input({ value: d.acheteur_morale_capital, onChange: (e) => updateD({ acheteur_morale_capital: e.target.value }) })}</div>
                <div style={{ gridColumn: "1 / -1" }}>{label("Siège social")}{input({ value: d.acheteur_morale_siege, onChange: (e) => updateD({ acheteur_morale_siege: e.target.value }) })}</div>
                <div>{label("RC")}{input({ value: d.acheteur_morale_rc, onChange: (e) => updateD({ acheteur_morale_rc: e.target.value }) })}</div>
                <div>{label("ICE")}{input({ value: d.acheteur_morale_ice, onChange: (e) => updateD({ acheteur_morale_ice: e.target.value }) })}</div>
                <div>{label("Représentant — Prénom")}{input({ value: d.acheteur_morale_representant_prenom, onChange: (e) => updateD({ acheteur_morale_representant_prenom: e.target.value }) })}</div>
                <div>{label("Représentant — Nom")}{input({ value: d.acheteur_morale_representant_nom, onChange: (e) => updateD({ acheteur_morale_representant_nom: e.target.value }) })}</div>
                <div style={{ gridColumn: "1 / -1" }}>
                  {label("Qualité du représentant")}
                  {input({
                    value: d.acheteur_morale_qualite,
                    onChange: (e) => updateD({ acheteur_morale_qualite: e.target.value }),
                    placeholder: "ex. Gérant, Directeur général…",
                  })}
                </div>
                <div>{label("Email société")}{input({ type: "email", value: d.acheteur_morale_email, onChange: (e) => updateD({ acheteur_morale_email: e.target.value }) })}</div>
                <div>{label("Téléphone")}{input({ value: d.acheteur_morale_telephone, onChange: (e) => updateD({ acheteur_morale_telephone: e.target.value }) })}</div>
              </div>
            )}
          </div>

          {/* C — Bien */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Désignation du Bien Immobilier</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ gridColumn: "1 / -1" }}>{label("Nom du projet")}{input({ value: d.nom_projet, onChange: (e) => updateD({ nom_projet: e.target.value }), placeholder: "ex. Atlas Bay" })}</div>
              <div>{label("N° villa / lot")}{input({ value: d.num_villa, onChange: (e) => updateD({ num_villa: e.target.value }) })}</div>
              <div>{label("N° de lot (promotion)")}{input({ value: d.num_lot, onChange: (e) => updateD({ num_lot: e.target.value }) })}</div>
              <div>{label("N° de lot foncier")}{input({ value: d.num_lot_foncier, onChange: (e) => updateD({ num_lot_foncier: e.target.value }) })}</div>
              <div>{label("Surface terrain (m²)")}{input({ value: d.surface_terrain, onChange: (e) => updateD({ surface_terrain: e.target.value }) })}</div>
              <div>{label("Surface RDC (m²)")}{input({ value: d.surface_rdc, onChange: (e) => updateD({ surface_rdc: e.target.value }) })}</div>
              <div>{label("Surface Étage (m²)")}{input({ value: d.surface_etage, onChange: (e) => updateD({ surface_etage: e.target.value }) })}</div>
              <div>{label("Surface Sous-sol (m²)")}{input({ value: d.surface_ssol, onChange: (e) => updateD({ surface_ssol: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("Adresse / localisation du projet")}{input({ value: d.adresse_projet, onChange: (e) => updateD({ adresse_projet: e.target.value }) })}</div>
              <div style={{ gridColumn: "1 / -1" }}>{label("Référence foncière (titre mère)")}{input({ value: d.ref_fonciere, onChange: (e) => updateD({ ref_fonciere: e.target.value }) })}</div>
            </div>
            <div
              style={{
                marginTop: 18,
                paddingTop: 16,
                borderTop: `1px solid ${BORDER}`,
              }}
            >
              <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 17, color: TAUPE }}>Identification de la Villa</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  {label("Type de villa")}
                  <select
                    value={d.type_villa}
                    onChange={(e) => updateD({ type_villa: e.target.value })}
                    style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 13 }}
                  >
                    <option value="Villa RDC">Villa RDC</option>
                    <option value="Villa R+1">Villa R+1</option>
                    <option value="Villa R+2">Villa R+2</option>
                    <option value="Duplex">Duplex</option>
                    <option value="Villa plain-pied">Villa plain-pied</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                {d.type_villa === "Autre" ? (
                  <div>{label("Préciser le type")}{input({ value: d.type_villa_autre, onChange: (e) => updateD({ type_villa_autre: e.target.value }) })}</div>
                ) : null}
                <div>
                  {label("Style architectural")}
                  <select
                    value={d.style_architectural}
                    onChange={(e) => updateD({ style_architectural: e.target.value })}
                    style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 13 }}
                  >
                    <option value="Contemporain">Contemporain</option>
                    <option value="Marocain traditionnel">Marocain traditionnel</option>
                    <option value="Méditerranéen">Méditerranéen</option>
                    <option value="Moderne minimaliste">Moderne minimaliste</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                {d.style_architectural === "Autre" ? (
                  <div>{label("Préciser le style")}{input({ value: d.style_architectural_autre, onChange: (e) => updateD({ style_architectural_autre: e.target.value }) })}</div>
                ) : null}
                <div>{label("Nombre de chambres")}{input({ type: "number", min: 0, value: d.nb_chambres, onChange: (e) => updateD({ nb_chambres: e.target.value }) })}</div>
                <div>{label("Nombre de salles de bain")}{input({ type: "number", min: 0, step: 0.5, value: d.nb_sdb, onChange: (e) => updateD({ nb_sdb: e.target.value }) })}</div>
                <div style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!d.villa_piscine}
                      onChange={(e) => updateD({ villa_piscine: e.target.checked })}
                    />
                    Présence piscine
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!d.villa_jardin}
                      onChange={(e) => updateD({ villa_jardin: e.target.checked })}
                    />
                    Présence jardin
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!d.villa_parking}
                      onChange={(e) => updateD({ villa_parking: e.target.checked })}
                    />
                    Présence parking
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!d.villa_sous_sol}
                      onChange={(e) => updateD({ villa_sous_sol: e.target.checked })}
                    />
                    Présence sous-sol
                  </label>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  {label("Équipements inclus")}
                  <textarea
                    value={d.equipements}
                    onChange={(e) => updateD({ equipements: e.target.value })}
                    placeholder="ex. cuisine équipée, climatisation, etc."
                    rows={3}
                    style={{
                      width: "100%",
                      padding: 10,
                      borderRadius: 8,
                      border: `1px solid ${BORDER}`,
                      fontFamily: FF_DM,
                      fontSize: 13,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  {label("Finitions")}
                  <select
                    value={d.finitions}
                    onChange={(e) => updateD({ finitions: e.target.value })}
                    style={{ width: "100%", maxWidth: 320, padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 13 }}
                  >
                    <option value="Standard">Standard</option>
                    <option value="Premium">Premium</option>
                    <option value="Luxe">Luxe</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* D — Prix */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Prix et Modalités de Paiement</h2>
            <div style={{ marginBottom: 14 }}>{label("Prix de vente total (MAD)")}{input({ value: d.prix_total, onChange: (e) => updateD({ prix_total: e.target.value }) })}</div>
            {!pctOk && (
              <p style={{ color: "#B85C3A", fontSize: 12, marginBottom: 8 }}>Somme des pourcentages : {sumPct.toFixed(2)}% (ajustez pour viser 100%)</p>
            )}
            <p style={{ fontSize: 12, color: "#8B7361", marginBottom: 12 }}>
              Le report sur chaque échéance est l&apos;écart du versement précédent : prévu − versé réel (manque à ajouter, excédent à déduire).
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {versements.map((row, i) => {
                const vd = versementDerive[i];
                if (!vd) return null;
                const rep = vd.reportIncoming || 0;
                const lastIdx = versements.length - 1;
                return (
                  <div
                    key={row.id}
                    style={{
                      border: `1px solid ${BORDER}`,
                      borderRadius: 12,
                      padding: 14,
                      background: "#FAFAF8",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: TAUPE }}>
                      V{row.num} — {row.label} — {vd.pct}%
                    </div>
                    <div style={{ fontSize: 12, color: "#8B7361", marginBottom: 8 }}>
                      Montant prévu : <span style={{ color: "#6b5a4a" }}>{formatMoney(vd.montant_prevu)} MAD</span>
                    </div>
                    {i > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                        {rep > 0.005 ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 8,
                              background: "#fdecea",
                              color: RED_MANQUE,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            + {formatMoney(rep)} MAD (report retard)
                          </span>
                        ) : null}
                        {rep < -0.005 ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 8,
                              background: "#e8f8ef",
                              color: VERT_EXC,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            − {formatMoney(Math.abs(rep))} MAD (excédent déduit)
                          </span>
                        ) : null}
                        {Math.abs(rep) <= 0.005 ? (
                          <span style={{ fontSize: 11, color: "#8B7361" }}>Aucun report depuis le versement précédent</span>
                        ) : null}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 18, fontWeight: 800, color: GOLD, margin: "10px 0" }}>
                      MONTANT DÛ : {formatMoney(vd.montant_du)} MAD
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end", marginBottom: 10 }}>
                      <div>
                        {label("% du prix (répartition)")}
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={row.pct}
                          onChange={(e) => handleVersementPctChange(i, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: `1px solid ${BORDER}`,
                            borderRadius: 8,
                            fontFamily: FF_DM,
                            fontSize: 13,
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div>
                        {label("Montant versé réel (MAD)")}
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={row.montant_reel === "" || row.montant_reel == null ? "" : row.montant_reel}
                          onChange={(e) => handleMontantReelChange(i, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            border: `1px solid ${BORDER}`,
                            borderRadius: 8,
                            fontFamily: FF_DM,
                            fontSize: 13,
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      {label("Date d&apos;échéance")}
                      <input
                        type="date"
                        value={row.date || ""}
                        onChange={(e) => updateVersement(i, { date: e.target.value })}
                        disabled={i === 0 || i === lastIdx}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: `1px solid ${BORDER}`,
                          fontFamily: FF_DM,
                          fontSize: 13,
                          minWidth: 160,
                        }}
                      />
                      <span style={{ marginLeft: 8, fontSize: 11, color: "#8B7361" }}>
                        {i === 0 ? "à la signature" : i === lastIdx ? "à la livraison" : "échéance intermédiaire"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop: 20,
                padding: 16,
                borderRadius: 12,
                border: `1px solid ${BORDER}`,
                background: WHITE,
              }}
            >
              <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 17 }}>Appels à paiement</h3>
              {versementDerive.map((vd, i) => (
                <div
                  key={versements[i]?.id || `ap-${i}`}
                  style={{
                    fontSize: 12,
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: i < versementDerive.length - 1 ? `1px dashed ${BORDER}` : "none",
                    lineHeight: 1.5,
                  }}
                >
                  <div style={{ color: "#5c4a3a" }}>
                    <strong style={{ color: TAUPE }}>Versement {i + 1}</strong> : {formatMoney(vd.montant_prevu)} MAD
                    <br />
                    {i > 0 ? (
                      <>
                        + Report versement précédent : {formatMoney(vd.reportIncoming)} MAD
                        <br />
                      </>
                    ) : null}
                    <span style={{ fontWeight: 800, color: GOLD }}>= TOTAL DÛ : {formatMoney(vd.montant_du)} MAD</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 18 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 10, fontSize: 13 }}>
                <div>
                  <span style={{ color: "#8B7361" }}>Total prévu : </span>
                  <strong>{formatMoney(prixNum)} MAD</strong>
                </div>
                <div>
                  <span style={{ color: "#8B7361" }}>Total versé : </span>
                  <strong>{formatMoney(totalVerseReel)} MAD</strong>
                </div>
                <div>
                  <span style={{ color: "#8B7361" }}>Reste total : </span>
                  <strong style={{ color: resteAPayer > 0 ? RED_MANQUE : TAUPE }}>{formatMoney(resteAPayer)} MAD</strong>
                </div>
              </div>
              <div style={{ height: 10, borderRadius: 6, background: "#E8E0D6", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${progressionPct}%`,
                    height: "100%",
                    background: GOLD,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <p style={{ margin: "6px 0 0", fontSize: 11, color: "#8B7361" }}>
                Progression : {progressionPct.toFixed(1)} % du prix versé
              </p>
            </div>
          </div>

          {/* E — Délais */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Délais et Conditions</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>{label("Délai prévisionnel de livraison (mois)")}{input({ value: d.delai_livraison_mois, onChange: (e) => updateD({ delai_livraison_mois: e.target.value }) })}</div>
              <div>{label("Date de démarrage des travaux")}{input({ type: "date", value: d.date_demarrage_travaux, onChange: (e) => updateD({ date_demarrage_travaux: e.target.value }) })}</div>
              <div>{label("Prorogation possible (mois)")}{input({ value: d.prorogation_mois, onChange: (e) => updateD({ prorogation_mois: e.target.value }) })}</div>
              <div>{label("Indemnité de résiliation (%)")}{input({ value: d.indemnite_resiliation_pct, onChange: (e) => updateD({ indemnite_resiliation_pct: e.target.value }) })}</div>
            </div>
          </div>

          {/* F — Arbitrage */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Règlement des Litiges</h2>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={d.clause_arbitrage} onChange={(e) => updateD({ clause_arbitrage: e.target.checked })} />
              Inclure clause d&apos;arbitrage
            </label>
            <p style={{ fontSize: 12, color: "#8B7361", marginTop: 0 }}>Loi applicable : loi n° 95-17 relative à l&apos;arbitrage et à la médiation conventionnelle.</p>
            {d.clause_arbitrage && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>{label("Nom de l'arbitre")}{input({ value: d.nom_arbitre, onChange: (e) => updateD({ nom_arbitre: e.target.value }) })}</div>
                <div>{label("Siège de l'arbitrage (ville)")}{input({ value: d.siege_arbitrage, onChange: (e) => updateD({ siege_arbitrage: e.target.value }) })}</div>
                <div>{label("Langue")}{input({ value: d.langue_arbitrage, onChange: (e) => updateD({ langue_arbitrage: e.target.value }) })}</div>
                <div>{label("Honoraires arbitre (% du litige)")}{input({ value: d.honoraires_arbitre_pct, onChange: (e) => updateD({ honoraires_arbitre_pct: e.target.value }) })}</div>
              </div>
            )}
          </div>

          {/* IA */}
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Clause personnalisée (IA)</h2>
            <textarea
              value={iaPrompt}
              onChange={(e) => setIaPrompt(e.target.value)}
              placeholder="Décrivez la clause souhaitée (ex. clause de désistement, garantie bancaire…)"
              rows={3}
              style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 13, marginBottom: 8 }}
            />
            <button
              type="button"
              disabled={iaLoading}
              onClick={genererClauseIA}
              style={{
                padding: "8px 14px",
                border: "none",
                borderRadius: 8,
                background: GOLD,
                color: "#fff",
                fontWeight: 700,
                cursor: iaLoading ? "wait" : "pointer",
                fontFamily: FF_DM,
                fontSize: 12,
              }}
            >
              {iaLoading ? "Génération…" : "Générer une clause personnalisée"}
            </button>
            {iaError ? <p style={{ color: "#C0392B", fontSize: 12, marginTop: 8 }}>{iaError}</p> : null}
            {d.clause_ia_personnalisee ? (
              <div style={{ marginTop: 10 }}>
                {label("Clause ajoutée au contrat")}
                <textarea
                  value={d.clause_ia_personnalisee}
                  onChange={(e) => updateD({ clause_ia_personnalisee: e.target.value })}
                  rows={6}
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 12 }}
                />
              </div>
            ) : null}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>
            <button type="button" onClick={copierContrat} style={{ padding: "9px 14px", borderRadius: 8, border: `1px solid ${BORDER}`, background: WHITE, cursor: "pointer", fontWeight: 600 }}>
              Copier le contrat
            </button>
            <button type="button" onClick={versionImprimable} style={{ padding: "9px 14px", borderRadius: 8, border: `1px solid ${BORDER}`, background: WHITE, cursor: "pointer", fontWeight: 600 }}>
              Version imprimable
            </button>
            <button type="button" onClick={enregistrerDossier} style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: GOLD, color: "#fff", cursor: "pointer", fontWeight: 700 }}>
              Enregistrer le dossier
            </button>
          </div>
        </div>

        {/* Aperçu */}
        <div
          id="vefa-apercu"
          className="print-contract"
          style={{
            position: "sticky",
            top: 16,
            background: WHITE,
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            padding: 28,
            fontFamily: FF_CONTRACT,
            fontSize: 14,
            lineHeight: 1.55,
            color: TAUPE,
            maxHeight: "calc(100vh - 32px)",
            overflow: "auto",
            boxShadow: "0 8px 32px rgba(61,46,32,0.06)",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: contratHtml }} />
        </div>
      </div>
    </main>
  );
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripHtmlToText(html) {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
